import React, { useState, useContext, useEffect } from 'react';
import MatchGroupContext from '../../context/matchGroups/matchGroupContext';
import FriendContext from '../../context/friends/friendContext';
import './ShareGroupModal.css';

const ShareGroupModal = ({ isOpen, onClose, group }) => {
  const { inviteToGroup } = useContext(MatchGroupContext);
  const { friends, getFriends } = useContext(FriendContext);
  
  const [selectedFriend, setSelectedFriend] = useState('');
  const [permission, setPermission] = useState('edit');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (isOpen && friends.length === 0) {
      getFriends();
    }
  }, [isOpen, friends.length, getFriends]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFriend('');
      setPermission('edit');
      setMessage({ text: '', type: '' });
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedFriend) {
      setMessage({ text: 'Please select a friend to invite', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const result = await inviteToGroup(group._id, selectedFriend, permission);
      
      if (result.success) {
        setMessage({ text: 'Invitation sent successfully!', type: 'success' });
        setSelectedFriend('');
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setMessage({ text: result.error || 'Failed to send invitation', type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'An error occurred while sending invitation', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Filter out friends who are already shared with
  const availableFriends = friends.filter(friend => 
    !group?.sharedWith?.some(shared => shared.user === friend._id)
  );

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content share-group-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Share Group: {group?.name}</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-body">
          {/* Current shared users */}
          {group?.sharedWith && group.sharedWith.length > 0 && (
            <div className="shared-users-section">
              <h3>Currently Shared With:</h3>
              <div className="shared-users-list">
                {group.sharedWith.map((shared, index) => {
                  // Handle case where shared.user might be a string (ID) or object
                  const userName = typeof shared.user === 'object' && shared.user !== null
                    ? (shared.user.name || shared.user.username || 'Unknown User')
                    : 'Unknown User';

                  return (
                    <div key={index} className="shared-user-item">
                      <div className="user-info">
                        <span className="user-name">{userName}</span>
                        <span className={`permission-badge ${shared.permission}`}>
                          {shared.permission}
                        </span>
                        <span className={`status-badge ${shared.status}`}>
                          {shared.status}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Invite new friend */}
          <div className="invite-section">
            <h3>Invite a Friend:</h3>
            
            {availableFriends.length === 0 ? (
              <p className="no-friends-message">
                {friends.length === 0 
                  ? "You don't have any friends to invite yet. Add some friends first!"
                  : "All your friends have already been invited to this group."
                }
              </p>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label" htmlFor="friend-select">Select Friend:</label>
                  <select
                    id="friend-select"
                    value={selectedFriend}
                    onChange={(e) => setSelectedFriend(e.target.value)}
                    className="form-control"
                  >
                    <option value="">Choose a friend...</option>
                    {availableFriends.map(friend => (
                      <option key={friend._id} value={friend._id}>
                        {friend.name || friend.username}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="permission-select">Permission Level:</label>
                  <select
                    id="permission-select"
                    value={permission}
                    onChange={(e) => setPermission(e.target.value)}
                    className="form-control"
                  >
                    <option value="edit">Edit - Can add matches to the group</option>
                    <option value="view">View - Can only view group matches</option>
                  </select>
                </div>

                {message.text && (
                  <div className={`message ${message.type}`}>
                    {message.text}
                  </div>
                )}

                <div className="modal-actions">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary" 
                    disabled={loading || !selectedFriend}
                  >
                    {loading ? 'Sending...' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareGroupModal;